/**
 * TIP Shell — THE RECORD (trusted chrome; parent DOM only).
 *
 * The tenant-wide, plain-English, hash-chained audit ledger, rendered as a sheet.
 * *"Legibility is the security property"* — this component is what that sentence
 * looks like on a screen, and it is the closing beat of the district demo.
 *
 * WHO SEES IT — decided 2026-07-25 (docs/CONSOLE-UX-BACKLOG.md item 4), and the
 * reason this file exists as its own module:
 *
 *   • DISTRICT (admin) console — YES. This is the seat the record was written for.
 *     An administrator reading "who did what, to whose data, under what authority"
 *     is the whole governance argument.
 *   • TEACHER console — NO. It used to render here too, and Steve's verdict on
 *     seeing it live was flat: *"A teacher doesn't care about that stuff in 'The
 *     Record' at all."* He was right. A teacher gets their confirmation from the
 *     inline outcome sentence of the action they just took ("The link is attested
 *     and on the record"); a tenant-wide chain of everyone's events is someone
 *     else's job, and it was costing the teacher half the screen.
 *
 * It previously lived in console.tsx and was imported FROM there by admin.tsx —
 * which meant the teacher console owned code only the district console rendered.
 * Moving it here is the same component, one honest import away from both seats.
 *
 * Data shown: whatever the server already decided to put in a record line. This
 * file adds no field and reveals nothing the ledger did not already say — see
 * `compactIds` for the one display-only transform.
 */

/** True for the ledger's closing honesty statements (they set apart, below the entries). */
function isHonestyLine(l: string): boolean {
  return /^(\d+ name|0 external|at least \d+ external|External network calls|No app activity)/.test(l);
}

/** DISPLAY-only compaction of surrogate uuids inside record sentences (the full
 *  sentence rides in the tooltip; the hash chain is the authority, not the view).
 *  Exported: the District Console compacts the same surrogates in its roster findings. */
export function compactIds(l: string): string {
  return l.replace(
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
    (m) => `${m.slice(0, 4)}…${m.slice(-4)}`,
  );
}

/** The tenant-wide audit ledger sheet. Rendered by the District Console. */
export function TheRecord({ audit, busy, onRefresh }: {
  audit: string[] | null;
  busy: string | null;
  onRefresh(): void;
}) {
  const lines = audit ?? [];
  // The server's first line is a header sentence; the sheet's own header
  // replaces it. Honesty tallies move to the closing statement.
  const entries = lines.filter((l, i) => i > 0 && !isHonestyLine(l));
  const closing = lines.filter((l) => isHonestyLine(l));

  return (
    <aside class="record-sheet">
      <header>
        <h3>
          The Record
          <button type="button" class="quiet" disabled={busy !== null} onClick={onRefresh}>
            refresh
          </button>
        </h3>
        <p class="kept">kept in plain English, hash-chained, tenant-wide</p>
      </header>
      {audit === null ? (
        <p style="color:var(--ink-faint);font-size:14px;">
          The record is not readable right now. It is still being kept.
        </p>
      ) : (
        <>
          <div class="record-entries">
            {entries.map((l, i) => (
              <div class={`rec${l.startsWith('⚠') ? ' warn' : ''}`} key={`${i}-${l.slice(0, 24)}`}>
                <span class="t">{String(i + 1).padStart(3, '0')}</span>
                <span class="s" title={l}>{compactIds(l)}</span>
              </div>
            ))}
            {entries.length === 0 ? (
              <p style="color:var(--ink-faint);font-size:14px;">Nothing on the record yet.</p>
            ) : null}
          </div>
          {closing.length > 0 ? (
            <div class="record-close">
              {closing.map((l, i) => (
                <div class="s" key={i}>✓ {l}</div>
              ))}
            </div>
          ) : null}
        </>
      )}
    </aside>
  );
}
